import PropTypes from 'prop-types';

import { getContent, isDDEnv, isWHEnv } from '@/utils/helper';
import { useIsKorean } from '@/utils/useLocale';

import Text from '@/atoms/Text/Text';
import RichText from '@/molecules/RichText/RichText';
import ContentGrid from '@/organisms/ContentGrid/ContentGrid';

import { TEXT_BASE_CLASS } from '@/atoms/Text/Text.constants';

import cls from 'classnames';

function Cut({ assets = [], entries = [], fields = null }) {
  const {
    name,
    title,
    slug,
    vector,
    pages,
    primaryDescription,
    aka,
    portion,
    texture,
    tenderness,
    callToAction,
    backgroundImage,
  } = fields;
  const vectorSrc = vector
    ? getContent(vector.sys.id, assets)?.fields?.file?.url
    : null;

  fields.cards = fields.dynamicCards;
  if (pages) {
    const pagesCard = pages.map((p) => ({
      primaryCTA: p.fields.slug,
      tags: p.fields.tags,
      title: p.fields.title,
      text: p.fields.previewText ?? null,
      cloudinaryAssets: p.fields.cloudinaryPreviewAssets ?? null,
      dynamicAsset: p.fields.previewAsset ?? null,
    }));
    fields.cards = (fields?.cards || []).concat(pagesCard);
  }

  if (fields?.cards?.length === 0) {
    return <></>;
  }

  const isDD = isDDEnv();
  const isWH = isWHEnv();

  const CutCard = (
    <div
      className={cls(
        'aaco-card__content bg-white h-full flex flex-col pt-[35px] px-[30px] pb-6 dd-kr:p-10 dd-en:p-10 mb-10 justify-between dd-en:shadow-xl dd-kr:shadow-xl dd-en:rounded-[8px] dd-kr:rounded-[8px]',
        {
          'md:justify-start bg-eucalyptus border-1 border-solid border-black': isWH,
        }
      )}
      style={{
        backgroundImage: `url(${backgroundImage?.[0]?.original_secure_url})`,
      }}
      id={slug}
    >
      <div className="flex flex-col">
        {vectorSrc && (
          <div>
            <img
              src={vectorSrc}
              className="w-[52px] md:w-[55px] h-[38px] md:h-[35.88px] dd-en:w-1/4 dd-kr:w-1/4 dd-kr:h-full dd-en:h-full"
              alt={name}
            />
          </div>
        )}
        <div className="mt-[11px] mb-[7px] md:mb-1 dd-kr:my-3 dd-en:my-3">
          <Text
            tag="h5"
            style={cls(
              'text-dark-warm-grey dd-kr:text-actual-dd-gold dd-en:text-actual-dd-gold dd-kr:font-h5 dd-en:font-h5 dd-kr:text-h5 dd-en:text-h5',
              {
                '!text-black': isWH,
              }
            )}
          >
            {isDD ? title : name}
          </Text>
        </div>
        {primaryDescription &&
          primaryDescription.content.length > 0 &&
          primaryDescription.content.map(({ nodeType, content }, index) => (
            <RichText
              entries={entries}
              nodeType={nodeType}
              content={content}
              key={index}
              richTextStyle={{
                paragraph: {
                  customFont:
                    TEXT_BASE_CLASS['body'] +
                    ' text-acacia dd-kr:text-body-text-grey dd-en:text-body-text-grey' +
                    cls({ ' !text-black': isWH }),
                  blockquoteFont:
                    TEXT_BASE_CLASS['blockquote'] +
                    ' text-acacia dd-kr:text-body-text-grey dd-en:text-body-text-grey' +
                    cls({ ' !text-black': isWH }),
                },
              }}
            />
          ))}
        {aka && (
          <div className={cls(
            'mt-[34px] py-[9px] border-t border-warm-grey grid grid-cut-col grid-cols-5 dd-kr:hidden dd-en:hidden',
            { '!border-black border-b-1 !mt-6': isWH }
          )}>
            <div className="col-span-2">
              <Text style={cls('text-dark-warm-grey', { '!text-black': isWH })}>Cuts:</Text>
            </div>
            <div className="col-span-3">
              <Text style={cls('text-acacia', { '!text-black': isWH })}>{aka}</Text>
            </div>
          </div>
        )}
        {portion && (
          <div className={cls(
            'py-[9px] border-t border-warm-grey grid grid-cut-col grid-cols-5 dd-kr:hidden dd-en:hidden',
            { '!border-black border-b-1 !border-t-0': isWH }
          )}>
            <div className="col-span-2">
              <Text style={cls('text-dark-warm-grey', { '!text-black': isWH })}>Portion:</Text>
            </div>
            <div className="col-span-3">
              <Text style={cls('text-acacia', { '!text-black': isWH })}>{portion}</Text>
            </div>
          </div>
        )}
        {texture && (
          <div className={cls(
            'py-[9px] border-t border-warm-grey grid grid-cut-col grid-cols-5 dd-kr:hidden dd-en:hidden',
            { '!border-black border-b-1 !border-t-0': isWH }
          )}>
            <div className="col-span-2">
              <Text style={cls('text-dark-warm-grey', { '!text-black': isWH })}>Texture:</Text>
            </div>
            <div className="col-span-3">
              <Text style={cls('text-acacia', { '!text-black': isWH })}>{texture}</Text>
            </div>
          </div>
        )}
        {tenderness && (
          <div className={cls(
            'py-[9px] border-y border-warm-grey grid grid-cut-col grid-cols-5 dd-kr:hidden dd-en:hidden',
            { '!border-black border-b-1 !border-t-0': isWH }
          )}>
            <div className="col-span-2">
              <Text style={cls('text-dark-warm-grey', { '!text-black': isWH })}>Tenderness:</Text>
            </div>
            <div className="col-span-3">
              <Text style={cls('text-acacia', { '!text-black': isWH })}>{tenderness}</Text>
            </div>
          </div>
        )}
      </div>
      {/* Commenting the CTA below for Cards in this page /our-cuts#bolar-blade-steaks */}
      {/* <div className="!mt-[24px]">
        {isDD && cta && (
          <Cta
            buttonProps={{
              name: cta.name,
              type: cta.style ?? CtfCtaStyle.DD_SECONDARY,
              style: 'w-fit',
            }}
            iconProps={{
              name: cta.icon ?? 'external-link',
              height: 15,
              width: 15,
              style: 'text-actual-dd-gold',
            }}
            linkProps={{
              url: cta?.link,
              page: cta?.page
                ? getContent(cta?.page?.sys?.id, entries)?.fields?.slug
                : null,
            }}
            onClick={(e, { localUrl, url, name }) =>
              analytics({
                window,
                document,
                detail: getClickCtaCutDetail(e, {
                  url,
                  elementName: name,
                }),
              })
            }
          />
        )}
        {!isDD && (
          <Cta
            buttonProps={{
              name: isKorean ? '자세히 보기' : 'Explore this Cut',
              type: CtfCtaStyle.PRIMARY,
              style: 'w-fit',
            }}
            iconProps={{
              name: 'arrow-right',
              height: 15,
              width: 15,
              style: 'text-acacia',
            }}
            linkProps={{
              url: `/cuts?cuts=${slug}`,
            }}
            onClick={(e, { localUrl, url, name }) =>
              analytics({
                window,
                document,
                detail: getClickCtaCutDetail(e, {
                  url,
                  elementName: name,
                }),
              })
            }
          />
        )}
      </div> */}
    </div>
  );

  return (
    <div
      className={cls("aaco-cut-grid md:mx-auto py-6 w-full", {
        "container": isWH,
        "xl:container": isDD,
      })}
      id={slug}
    >
      <div
        className={
          fields?.cards?.length > 0
            ? 'grid grid-cols-4 w-full overflow-x-scroll scrollbar-hide xl:overflow-x-visible'
            : 'grid grid-cols-4 w-full'
        }
      >
        <div className="col-span-4 xl:ml-0 w-full h-full pb-10 md:pb-0 flex flex-col lg:flex-row">
          <ContentGrid
            fields={fields}
            assets={assets}
            entries={entries}
            noPadding={true}
            columnLayout
            leftContent={
              <div className="aaco-card aaco-cut-card">{CutCard}</div>
            }
            overflowX
          />
        </div>
      </div>
    </div>
  );
}

// @TODO: define real props for the Header based on it requirements
Cut.propTypes = {
  assets: PropTypes.array,
  entries: PropTypes.array,
  fields: PropTypes.object,
};

export default Cut;
